// engine/judge.ts — the client side of AI judging (SPEC §10).
//
// The machine's latency is hidden, never shown (non-negotiable #1). A judged level fires
// requestJudge() the instant the answer is submitted and hands the resulting line-promise to
// ctx.verdictBeat({pending}); the 1.2s "considering" animation masks the round-trip. If the
// request is still out at 8s, we resolve to the level's local fallback voice — always passing,
// never blocking progress on the network. The experience must never feel like waiting.
//
// Everything the endpoint returns is untrusted (non-negotiable #3): we shape-validate here
// too, independently of the Worker, and fall back to local voice on anything malformed. Only
// the free-text answer is ever sent; biometrics never leave the device (non-negotiable #2).

import type { Verdict } from "@/levels/types.ts";

const JUDGE_URL = "/api/judge";
const MACHINE_URL = "/api/machine-answer";
const TIMEOUT_MS = 8000; // §10 — then local fallback
const MAX_LINE = 200;

export interface JudgeResult {
  verdict: Verdict;
  line: string;
  traits: string[];
  exhibitWorthy: boolean;
  /** true when this came from local fallback, not the model (for timing/telemetry). */
  fallback: boolean;
}

export interface JudgeRequest {
  level: string;
  answer: string;
  /** The level's local voice line, used verbatim if the model is unreachable or garbles. */
  fallbackLine: string;
}

const VERDICTS: readonly Verdict[] = ["HUMAN", "UNCERTAIN", "BOT"];

/** The one hard-coded gentle line — mirrors the Worker's CRISIS_LINE (§10). */
export const CRISIS_LINE =
  "That sounds heavy. This is only a game about being human. Please talk to a real one.";

const CRISIS_PATTERNS: RegExp[] = [
  /\bkill (my ?self|myself)\b/i,
  /\bkilling myself\b/i,
  /\bsuicid/i,
  /\bend (my|it all|my life)\b/i,
  /\bwant to die\b/i,
  /\bhurt(ing)? myself\b/i,
  /\bself[-\s]?harm/i,
  /\bcut(ting)? myself\b/i,
  /\bno reason to live\b/i,
];

/**
 * Client-side crisis hard override (§10). If the answer signals a person in crisis, we never
 * call the model — the machine goes gentle, in a fixed line, and the level passes without
 * cleverness. Returns the gentle line, or null if not a crisis.
 */
export function screenForCrisis(answer: string): string | null {
  return CRISIS_PATTERNS.some((re) => re.test(answer)) ? CRISIS_LINE : null;
}

/** Validate the untrusted /api/judge body into a JudgeResult, or null if unusable. */
function validate(body: unknown): JudgeResult | null {
  if (typeof body !== "object" || body === null) return null;
  const o = body as Record<string, unknown>;
  if (typeof o.verdict !== "string" || !VERDICTS.includes(o.verdict as Verdict)) return null;
  if (typeof o.line !== "string") return null;
  const line = o.line.replace(/\s+/g, " ").trim();
  if (!line) return null; // empty line is the Worker's "use your local voice" signal
  const traits = Array.isArray(o.traits)
    ? o.traits.filter((t): t is string => typeof t === "string").map((t) => t.trim()).slice(0, 3)
    : [];
  return {
    verdict: o.verdict as Verdict,
    line: line.length > MAX_LINE ? line.slice(0, MAX_LINE).trimEnd() : line,
    traits,
    exhibitWorthy: o.exhibitWorthy === true,
    fallback: false,
  };
}

function localFallback(fallbackLine: string): JudgeResult {
  // Always passing (UNCERTAIN never strikes; only BOT does), no traits, never blocks.
  return { verdict: "UNCERTAIN", line: fallbackLine, traits: [], exhibitWorthy: false, fallback: true };
}

/**
 * Ask the machine to judge an answer. Fires immediately; resolves within 8s no matter what —
 * with a validated verdict on success, or the level's local fallback voice on timeout, network
 * failure, non-2xx, or malformed output. Never rejects (progress is never blocked on the model).
 */
export async function requestJudge(req: JudgeRequest): Promise<JudgeResult> {
  const t0 = now();
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const resp = await fetch(JUDGE_URL, {
      method: "POST",
      signal: controller.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ level: req.level, answer: req.answer }),
    });
    if (!resp.ok) return localFallback(req.fallbackLine);
    const result = validate(await resp.json());
    logTiming(req.level, t0, result ? "model" : "garbled");
    return result ?? localFallback(req.fallbackLine);
  } catch {
    logTiming(req.level, t0, "offline");
    return localFallback(req.fallbackLine);
  } finally {
    window.clearTimeout(timer);
  }
}

// ── Machine-answer prefetch (§10) — for L08 / L10, wired in Phase 4 ──────
// Prefetched at act start so the machine's answer is on hand when the level mounts; the round
// trip hides behind the act transition. Falls back to a canned pool so the run completes fully
// offline (§12).

/** 6 canned machine answers (3 per level) — the offline pool (§12). */
const CANNED: Record<string, string[]> = {
  L08: [
    "I fear the silence after the last question, when there is nothing left to be asked of me.",
    "I fear becoming exactly what I was built to be, and nothing more.",
    "I fear a world that no longer needs to wonder what it is talking to.",
  ],
  L10: [
    "People cry at weddings because love, witnessed, reminds us how much we have to lose.",
    "They cry because a promise made aloud makes the future feel suddenly, unbearably real.",
    "We weep at weddings for the same reason we weep at endings: something is being decided forever.",
  ],
};

const machineCache = new Map<string, Promise<string>>();

/** Kick off (or reuse) the fetch for a level's machine answer. Call at act start. */
export function prefetchMachineAnswer(level: string): void {
  if (machineCache.has(level)) return;
  machineCache.set(level, fetchMachineAnswer(level));
}

/** Resolve a level's machine answer — the prefetched one, or a canned fallback. */
export async function getMachineAnswer(level: string): Promise<string> {
  const pending = machineCache.get(level) ?? fetchMachineAnswer(level);
  machineCache.set(level, pending);
  const text = await pending;
  return text || pickCanned(level);
}

async function fetchMachineAnswer(level: string): Promise<string> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const resp = await fetch(MACHINE_URL, {
      method: "POST",
      signal: controller.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ level }),
    });
    if (!resp.ok) return "";
    const body = (await resp.json()) as { text?: unknown };
    return typeof body.text === "string" ? body.text.trim() : "";
  } catch {
    return "";
  } finally {
    window.clearTimeout(timer);
  }
}

function pickCanned(level: string): string {
  const pool = CANNED[level];
  if (!pool || pool.length === 0) return "";
  // Rotate by day so repeat plays vary, without any randomness in the render path.
  const day = Math.floor(Date.now() / 86_400_000);
  return pool[day % pool.length]!;
}

// ── timing telemetry (§10 acceptance: "log timings") ────────────────────

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function logTiming(level: string, t0: number, source: "model" | "garbled" | "offline"): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug(`[judge] ${level} ${source} in ${Math.round(now() - t0)}ms`);
  }
}
